// src/components/DefectTable.js
import React, {useState, useEffect} from 'react';
import { Camera } from 'lucide-react';

const DefectTable = ({ defects, setDefects }) => {
  const updateRow = (index, field, value) => {
    setDefects(prev =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const [today, setToday] = useState('');

  useEffect(() => {
    const date = new Date();
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const dd = String(date.getDate()).padStart(2, '0');
    setToday(`${yyyy}-${mm}-${dd}`);
  }, []);

  const handlePictureChange = (index, files) => {
    const newFiles = Array.from(files).filter(f => f.type.startsWith('image/'));

    setDefects(prev =>
      prev.map((row, i) =>
        i === index
          ? { ...row, pictures: [...row.pictures, ...newFiles] }
          : row
      )
    );
  };

  const removePicture = (rowIndex, picIndex) => {
    setDefects(prev =>
      prev.map((row, i) =>
        i === rowIndex
          ? {
              ...row,
              pictures: row.pictures.filter((_, j) => j !== picIndex),
            }
          : row
      )
    );
  };

  const isRowComplete = row => {
    return (
      row.serial?.trim() &&
      row.description?.trim() &&
      row.observationDate &&
      row.pictures.length > 0
    );
  };

  if (defects.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 mt-8 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          Defect Observations
        </h2>
        <div className="text-center py-12 text-gray-500 border-2 border-dashed border-gray-300 rounded-xl">
          <p className="text-lg">
            Enter <strong>Total Reject Quantity</strong> above to generate defect rows.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mt-4 mb-4 border border-gray-200">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">
        Defect Observations ({defects.length} Rejects - All Fields Required)
      </h2>

      <div className="mt-4 p-4 bg-yellow-50 border border-yellow-300 rounded-lg text-yellow-800">
        <strong>Note:</strong> All {defects.length} defect rows are mandatory. 
        Please fill Serial, Description, Date, and upload at least one picture per row.
      </div>

      <div className="overflow-x-auto rounded-lg mt-4">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-700 text-white">
              <th className="border border-gray-300 px-4 py-3 text-left text-sm font-bold">Sr. No</th>
              <th className="border border-gray-300 px-4 py-3 text-left text-sm font-bold">Serial *</th>
              <th className="border border-gray-300 px-4 py-3 text-left text-sm font-bold">Description *</th>
              <th className="border border-gray-300 px-4 py-3 text-left text-sm font-bold">Observation Date *</th>
              <th className="border border-gray-300 px-4 py-3 text-left text-sm font-bold">Pictures (Min 1) *</th>
            </tr>
          </thead>
          <tbody>
            {defects.map((defect, index) => {
              const incomplete = !isRowComplete(defect);

              return (
                <tr key={index} className={incomplete ? 'bg-red-50' : 'bg-green-50'}>
                  <td className="border border-gray-300 px-4 py-3 text-center font-bold text-gray-700">
                    {index + 1}
                  </td>

                  <td className="border border-gray-300 px-4 py-3">
                    <input
                      type="text"
                      value={defect.serial || ''}
                      onChange={e => updateRow(index, 'serial', e.target.value)}
                      placeholder="SN123456"
                      className={`w-full px-3 py-2 border-2 rounded focus:ring-2 focus:ring-blue-500 outline-none ${
                        !defect.serial?.trim() ? 'border-red-500' : 'border-green-500'
                      }`}
                    />
                  </td>

                  <td className="border border-gray-300 px-4 py-3">
                    <textarea
                      value={defect.description || ''}
                      onChange={e => updateRow(index, 'description', e.target.value)}
                      rows="2"
                      placeholder="Describe the defect..."
                      className={`w-full px-3 py-2 border-2 rounded focus:ring-2 focus:ring-blue-500 outline-none resize-none ${
                        !defect.description?.trim() ? 'border-red-500' : 'border-green-500'
                      }`}
                    />
                  </td>

                  <td className="border border-gray-300 px-4 py-3">
                    <input
                      type="date"
                      value={defect.observationDate || ''}
                      max={today}
                      onChange={e => updateRow(index, 'observationDate', e.target.value)}
                      className={`w-full px-3 py-2 border-2 rounded focus:ring-2 focus:ring-blue-500 outline-none ${
                        !defect.observationDate ? 'border-red-500' : 'border-green-500'
                      }`}
                    />
                  </td>

                  <td className="border border-gray-300 px-4 py-3">
                    <div className="space-y-3">
                      <label className="flex items-center gap-2 cursor-pointer text-blue-600 font-medium">
                        <Camera className="w-5 h-5" />
                        <span>Add Pictures *</span>
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={e => handlePictureChange(index, e.target.files)}
                          className="hidden"
                        />
                      </label>

                      {/* Simple file list with remove buttons */}
                      <div className="space-y-1">
                        {defect.pictures.length === 0 ? (
                          <span className="text-red-600 text-sm">No images selected</span>
                        ) : (
                          <>
                            <p className="text-green-700 font-medium text-sm">
                              {defect.pictures.length} image(s) selected
                            </p>
                            <ul className="text-xs text-gray-600 max-h-32 overflow-y-auto">
                              {defect.pictures.map((file, picIdx) => (
                                <li key={picIdx} className="flex items-center justify-between py-1">
                                  <span className="truncate max-w-[200px]">
                                    {file.name} ({(file.size / 1024).toFixed(1)} KB)
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => removePicture(index, picIdx)}
                                    className="ml-2 text-red-600 hover:text-red-800 font-bold"
                                  >
                                    ×
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DefectTable;